import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { forkJoin, Observable, of } from 'rxjs';
import { concatMap, expand, map, share, takeWhile, toArray } from 'rxjs/operators';

import { Paginated } from '../entities/paginated.entity';
import { SearchParams, SearchParamsPostBody } from '../entities/search-params.entity';
import { ShAbstractHttpService } from './http.abstract.service';

@Injectable({ providedIn: 'root' })
export class ShPaginationService {
    constructor(private readonly http: ShAbstractHttpService) {}

    getAll<T>(
        request: string,
        params: HttpParams = new HttpParams(),
        headers: HttpHeaders = new HttpHeaders(),
        perRequest: number = 100
    ): Observable<T[]> {
        return this.get(request, params, headers, 0, perRequest).pipe(
            expand(({ StartIndex, NrOfResults, TotalNrOfResults }) => {
                if (StartIndex + NrOfResults === TotalNrOfResults) {
                    return of({
                        NrOfResults: 0,
                        StartIndex: TotalNrOfResults,
                        TotalNrOfResults,
                        Results: []
                    });
                }
                return this.get(request, params, headers, StartIndex + NrOfResults, NrOfResults);
            }),
            takeWhile(({ Results }) => Results.length > 0),
            concatMap(({ Results }) => Results as T[]),
            toArray(),
            share()
        );
    }

    getAllThroughPost<T>(
        request: string,
        params: SearchParamsPostBody | SearchParams,
        headers: HttpHeaders = new HttpHeaders(),
        perRequest: number = 100
    ): Observable<T[]> {
        return this.post(request, params, headers, 0, perRequest).pipe(
            expand(({ StartIndex, NrOfResults, TotalNrOfResults }) => {
                if (StartIndex + NrOfResults === TotalNrOfResults) {
                    return of({
                        NrOfResults: 0,
                        StartIndex: TotalNrOfResults,
                        TotalNrOfResults,
                        Results: []
                    });
                }
                return this.post(request, params, headers, StartIndex + NrOfResults, NrOfResults);
            }),
            takeWhile(({ Results }) => Results.length > 0),
            concatMap(({ Results }) => Results as T[]),
            toArray(),
            share()
        );
    }

    getMultiple<T>(
        request: string,
        params: HttpParams = new HttpParams(),
        headers: HttpHeaders = new HttpHeaders(),
        numberOfResults: number,
        perRequest: number = 100
    ): Observable<T[]> {
        return this.get(request, params, headers, 0, perRequest).pipe(
            expand(({ StartIndex, NrOfResults, TotalNrOfResults }) => {
                const currentNumberOfResults: number = StartIndex + NrOfResults;
                if (currentNumberOfResults === TotalNrOfResults || currentNumberOfResults === numberOfResults) {
                    return of({
                        NrOfResults: 0,
                        StartIndex: TotalNrOfResults,
                        TotalNrOfResults,
                        Results: []
                    });
                }
                return this.get(request, params, headers, StartIndex + NrOfResults, NrOfResults);
            }),
            takeWhile(({ Results }) => Results.length > 0),
            concatMap(({ Results }) => Results as T[]),
            toArray(),
            share()
        );
    }

    getMultipleThroughPost<T>(
        request: string,
        params: SearchParamsPostBody | SearchParams,
        headers: HttpHeaders = new HttpHeaders(),
        numberOfResults: number,
        perRequest: number = 100
    ): Observable<T[]> {
        const posts = [];

        for (let startIndex = 0; startIndex < numberOfResults; startIndex += perRequest) {
            posts.push(this.post(request, params, headers, startIndex, perRequest));
        }

        return forkJoin(posts).pipe(
            map(posts => posts.flatMap(post => post.Results) as T[]),
            share()
        );
    }

    get<T>(
        request: string,
        params: HttpParams = new HttpParams(),
        headers: HttpHeaders = new HttpHeaders(),
        startIndex: number = 0,
        nrOfResults: number = 100
    ): Observable<Paginated<T>> {
        const requestParams: HttpParams = params
            .append('startIndex', startIndex.toString())
            .append('nrOfResults', nrOfResults.toString());
        return this.http.get<Paginated<T>>(request, requestParams, headers);
    }

    post<T>(
        request: string,
        params: SearchParamsPostBody | SearchParams,
        headers: HttpHeaders = new HttpHeaders(),
        startIndex: number = 0,
        nrOfResults: number = 100
    ): Observable<Paginated<T>> {
        const requestParams: SearchParams = {
            ...params,
            StartIndex: startIndex.toString(),
            NrOfResults: nrOfResults.toString()
        };

        return this.http.post<Paginated<T>>(request, requestParams, headers);
    }
}
